import { DateType } from "@date-io/type";
import { TextField, TextFieldProps } from "@mui/material";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers";
import { Controller, FieldValues, Path, useFormContext, Control } from "react-hook-form";
import {useTranslation} from "react-i18next";

type FormDatePickerProps<IFormValues extends FieldValues = FieldValues> = {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>
    label?: string,
    required?: boolean,
    textFieldProps?: Omit<TextFieldProps, 'name' | 'value' | 'label' | 'error'>
} & Omit<DatePickerProps<string, DateType>, 'renderInput' | 'value' | 'ref' | 'label' | 'onChange'> & {
    onChange?: DatePickerProps<string, DateType>['onChange']
}

const FormDatePicker = <IFormValues extends FieldValues = FieldValues>({
    name,
    control,
    label,
    required,
    textFieldProps,
    ...props
}: FormDatePickerProps<any>) => {
    const {t} = useTranslation(['network', 'translation']);
    const formContext = useFormContext<IFormValues>();

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            render={({ field, fieldState, formState }) =>
                <DatePicker
                    {...props}
                    ref={field.ref}
                    value={field.value}
                    onChange={(value) => {
                        field.onChange(value);
                        props?.onChange?.(value);
                    }}
                    label={label}
                    inputFormat={props?.inputFormat ?? 'DD/MM/YYYY'}
                    disabled={props.disabled ?? formState.isSubmitting}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            {...textFieldProps}
                            onBlur={field.onBlur}
                            error={Boolean(fieldState.error)}
                            helperText={t(!!fieldState.error?.message ? fieldState.error.message : '') ?? textFieldProps?.helperText}
                            fullWidth
                            required={required}
                        />
                    }
                />
            }
        />
    )
};

export default FormDatePicker;